import React, { useEffect } from 'react';
import { Check } from 'lucide-react';
import { ILMSAlert } from '../../../components/ILMSAlert';
import { ILMSButton } from '../../../components/ILMSButton';
import { ILMSFormField, ILMSSelect } from '../../../components/ILMSForm';
import { cn } from '../../../utils/cn';
import { DEFAULT_THEME, previewTheme, shadeHex, THEME_PRESETS, type SiteTheme } from '../../../utils/site';
import { PhotoPicker } from './PhotoPicker';
import { SaveBar } from './SaveBar';
import { useSiteDraft } from './useSiteDraft';

const FONTS: SiteTheme['font'][] = ['Inter', 'Poppins', 'Roboto', 'Open Sans', 'Lato', 'Noto Sans', 'Mukta'];

// Colour theme, font, corners and the logo - for the web app, the website
// and the login page. Changes show live on this page; Save applies them for everyone.
export function ThemePanel() {
  const { draft, set, save, saving, error, saved, dirty, canEdit } = useSiteDraft();
  const theme = draft.theme;
  const setTheme = (patch: Partial<SiteTheme>) => set('theme', { ...theme, ...patch });

  useEffect(() => { previewTheme(theme); }, [theme]);
  useEffect(() => () => previewTheme(null), []);

  return (
    <div className="flex flex-col gap-5">
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
      <div>
        <p className="text-label text-ink-900 mb-2">Colour theme</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {THEME_PRESETS.map((p) => {
            const on = theme.preset === p.id;
            return (
              <button
                key={p.id} type="button" disabled={!canEdit} onClick={() => setTheme({ preset: p.id, primary: p.primary, accent: p.accent })}
                className={cn('flex items-center gap-2 rounded-md border p-2 text-left text-secondary', on ? 'border-brand-green-600 ring-2 ring-brand-green-200' : 'border-ink-300/30 hover:border-ink-300')}
              >
                <span className="flex shrink-0 overflow-hidden rounded">
                  <span className="h-7 w-5" style={{ background: shadeHex(p.primary, 700) }} />
                  <span className="h-7 w-5" style={{ background: p.primary }} />
                  <span className="h-7 w-5" style={{ background: p.accent }} />
                </span>
                <span className="min-w-0 flex-1 truncate">{p.label}</span>
                {on && <Check className="h-4 w-4 text-brand-green-700 shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <ILMSFormField label="Main colour" htmlFor="primary" helperText="Menus, buttons, headings.">
          <div className="flex items-center gap-2">
            <input id="primary" type="color" disabled={!canEdit} value={theme.primary} onChange={(e) => setTheme({ preset: 'custom', primary: e.target.value })} className="h-10 w-14 cursor-pointer rounded border border-ink-300/40" />
            <span className="text-secondary text-ink-500 uppercase">{theme.primary}</span>
          </div>
        </ILMSFormField>
        <ILMSFormField label="Highlight colour" htmlFor="accent" helperText="Badges, Scan button, highlights.">
          <div className="flex items-center gap-2">
            <input id="accent" type="color" disabled={!canEdit} value={theme.accent} onChange={(e) => setTheme({ preset: 'custom', accent: e.target.value })} className="h-10 w-14 cursor-pointer rounded border border-ink-300/40" />
            <span className="text-secondary text-ink-500 uppercase">{theme.accent}</span>
          </div>
        </ILMSFormField>
        <ILMSFormField label="Font" htmlFor="font">
          <ILMSSelect id="font" disabled={!canEdit} value={theme.font} onChange={(e) => setTheme({ font: e.target.value as SiteTheme['font'] })}>
            {FONTS.map((f) => <option key={f} value={f}>{f}{f === 'Inter' ? ' (default)' : f === 'Mukta' ? ' (good with Kannada)' : ''}</option>)}
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Corners" htmlFor="corners">
          <ILMSSelect id="corners" disabled={!canEdit} value={theme.corners} onChange={(e) => setTheme({ corners: e.target.value as SiteTheme['corners'] })}>
            <option value="sharp">Sharp</option>
            <option value="normal">Normal (default)</option>
            <option value="round">Round</option>
          </ILMSSelect>
        </ILMSFormField>
      </div>

      {/* Live sample of the chosen theme - the whole page also shows it until you leave */}
      <div className="rounded-lg border border-ink-300/20 overflow-hidden max-w-3xl">
        <div className="flex">
          <div className="w-40 bg-white border-r border-ink-300/15 p-2 flex flex-col gap-1">
            <span className="rounded-md bg-brand-green-50 text-brand-green-800 px-2 py-1.5 text-secondary font-semibold">Dashboard</span>
            <span className="rounded-md px-2 py-1.5 text-secondary text-ink-600">Animals</span>
            <span className="rounded-md px-2 py-1.5 text-secondary text-ink-600">Dairy</span>
          </div>
          <div className="flex-1 bg-surface-subtle p-4 flex flex-col gap-3">
            <p className="text-section-title text-brand-green-900">Sample page</p>
            <div className="flex flex-wrap items-center gap-2">
              <ILMSButton size="sm">Save</ILMSButton>
              <ILMSButton size="sm" variant="outline">Cancel</ILMSButton>
              <span className="rounded-md bg-brand-gold-500 text-white px-3 py-1.5 text-secondary font-semibold">Scan</span>
              <span className="rounded-full bg-brand-gold-100 text-brand-gold-700 px-3 py-1 text-helper-text font-semibold">Badge</span>
            </div>
            <div className="rounded-lg bg-brand-green-800 text-white p-3 text-secondary">Login page / website band</div>
          </div>
        </div>
      </div>

      <div className="max-w-xl">
        <p className="text-label text-ink-900 mb-2">Logo</p>
        <PhotoPicker
          label="Farm logo" value={draft.logo} fallback="/logo-sm.jpeg" contain maxSide={600} disabled={!canEdit}
          help="Header, login page, website and the browser tab. A square PNG works best."
          onChange={(name) => set('logo', name)}
        />
        <p className="mt-1.5 text-helper-text text-ink-400">The Android app keeps its own built-in logo until the next app build.</p>
      </div>

      <SaveBar canEdit={canEdit} dirty={dirty} saving={saving} saved={saved} onSave={save}
        onReset={() => set('theme', DEFAULT_THEME)} resetLabel="Default colours" />
    </div>
  );
}
